import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, Star, Heart, Filter, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';

const safariPackages = [
  {
    id: 'finest',
    title: "12-Day Uganda's Finest",
    image: 'https://imgur.com/YmQ8sKD.jpg',
    location: 'Multiple Parks',
    duration: '12 days',
    groupSize: '2-6 people',
    price: 2400,
    rating: 4.9,
    reviews: 45,
    description: 'Experience the ultimate Ugandan adventure with this comprehensive safari package.',
    tag: 'Premium',
    highlights: [
      'Gorilla trekking',
      'Chimpanzee tracking',
      'Game drives',
      'Boat safaris'
    ]
  },
  {
    id: 'primate',
    title: '8-Day The Primate Express',
    image: 'https://imgur.com/PK2H77z.jpg',
    location: 'Multiple Parks',
    duration: '8 days',
    groupSize: '2-6 people',
    price: 2100,
    rating: 4.8,
    reviews: 38,
    description: 'Focus on Uganda\'s incredible primates including gorillas and chimpanzees.',
    tag: 'Popular',
    highlights: [
      'Gorilla trekking',
      'Chimpanzee tracking',
      'Nature walks',
      'Cultural visits'
    ]
  },
  {
    id: 'circuit',
    title: '24-Day The Grand Circuit',
    image: 'https://imgur.com/2xNtdHj.jpg',
    location: 'Multiple Parks',
    duration: '24 days',
    groupSize: '2-6 people',
    price: 5100,
    rating: 4.7,
    reviews: 52,
    description: 'A comprehensive exploration of Uganda\'s most iconic destinations.',
    tag: 'Best Value',
    highlights: [
      'Game drives',
      'Boat safaris',
      'Nature walks',
      'Cultural experiences'
    ]
  },
  {
    id: 'wildlife',
    title: 'Game Drives Uganda',
    image: 'https://imgur.com/WqH4Bly.jpg',
    location: 'Multiple Parks',
    duration: '4 days',
    groupSize: '2-6 people',
    price: 1200,
    rating: 4.7,
    reviews: 78,
    description: 'Experience Uganda\'s rich wildlife through expertly guided game drives in multiple national parks.',
    tag: 'Wildlife Focus',
    highlights: [
      'Multiple park game drives',
      'Professional wildlife guides',
      'Diverse ecosystems',
      'Photography opportunities'
    ]
  },
  {
    id: 'into-wild',
    title: 'Into The Wild',
    image: 'https://imgur.com/0voUAaS.jpg',
    location: 'Multiple Locations',
    duration: '12 days',
    groupSize: '2-6 people',
    price: 4099,
    rating: 4.9,
    reviews: 45,
    description: 'A comprehensive adventure through Uganda\'s most remote and pristine wilderness areas.',
    tag: 'Adventure',
    highlights: [
      'Remote park exploration',
      'Wildlife encounters',
      'Cultural experiences',
      'Adventure activities'
    ]
  },
  {
    id: 'big-five',
    title: 'The Big Five Safari',
    image: 'https://imgur.com/apzhfcl.jpg',
    location: 'Multiple Parks',
    duration: '7 days',
    groupSize: '2-6 people',
    price: 2200,
    rating: 4.9,
    reviews: 82,
    description: 'Track all members of the Big Five—Lions, Elephants, Leopards, Buffalos, and Rhinos.',
    tag: 'Big Five',
    highlights: [
      'Track all Big Five animals',
      'Multiple national parks',
      'River and land safaris',
      'Expert wildlife guides'
    ]
  },
  {
    id: 'birdwatching',
    title: 'Uganda Birdwatching Safari',
    image: 'https://imgur.com/7qDwljW.jpg',
    location: 'Multiple Locations',
    duration: '10 days',
    groupSize: '2-6 people',
    price: 2800,
    rating: 4.8,
    reviews: 45,
    description: 'Uganda is a birdwatcher\'s paradise, home to over 1,000 bird species!',
    tag: 'Birding',
    highlights: [
      'Over 1,000 bird species',
      'Multiple ecosystems',
      'Expert birding guides',
      'Photography opportunities'
    ]
  },
  {
    id: 'luxury',
    title: 'Uganda Luxury Safari & Gorilla Trekking',
    image: 'https://imgur.com/8xxc0ZF.jpg',
    location: 'Multiple Parks',
    duration: '8 days',
    groupSize: '2-6 people',
    price: 5000,
    rating: 4.9,
    reviews: 38,
    description: 'A high-end luxury safari that blends wildlife, comfort, and exclusivity.',
    tag: 'Luxury',
    highlights: [
      'Luxury accommodations',
      'Private charter flights',
      'Exclusive experiences',
      'Personal guide'
    ]
  },
  {
    id: 'kidepo',
    title: 'Kidepo Wilderness Safari',
    image: 'https://imgur.com/jLwqpmS.jpg',
    location: 'Kidepo Valley',
    duration: '6 days',
    groupSize: '2-6 people',
    price: 1800,
    rating: 4.9,
    reviews: 32,
    description: 'For travelers seeking raw, untouched wilderness in northeastern Uganda.',
    tag: 'Remote',
    highlights: [
      'Remote wilderness',
      'Unique wildlife',
      'Cultural experiences',
      'Scenic landscapes'
    ]
  }
];

const SafariPackages = () => {
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 6000]);
  const [durationFilter, setDurationFilter] = useState<string[]>([]);
  const { t } = useTranslation();

  const filteredPackages = safariPackages.filter(pkg => {
    const matchesSearch = pkg.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         pkg.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = pkg.price >= priceRange[0] && pkg.price <= priceRange[1];
    const matchesDuration = durationFilter.length === 0 || 
                           durationFilter.some(d => pkg.duration.includes(d));
    
    return matchesSearch && matchesPrice && matchesDuration;
  });

  const handleBookNow = (pkg: any) => {
    setSelectedPackage(pkg);
    setIsBookingModalOpen(true);
  };

  // Function to get the correct route path for each package
  const getPackageRoute = (packageId: string) => {
    switch (packageId) {
      case 'finest':
        return '/self-drive-safaris/ugandas-finest';
      case 'primate':
        return '/self-drive-safaris/primate-express';
      case 'circuit':
        return '/self-drive-safaris/grand-circuit';
      case 'wildlife':
        return '/experiences/4';
      case 'into-wild':
        return '/experiences/5';
      case 'big-five':
        return '/experiences/8';
      case 'birdwatching':
        return '/experiences/9';
      case 'luxury':
        return '/experiences/10';
      case 'kidepo':
        return '/experiences/11';
      default:
        return `/experiences/${packageId}`;
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/WqH4Bly.jpg"
            alt="Safari experience"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/uganda-trips" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">{t('pages.safariPackages.title')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('pages.safariPackages.subtitle')}
            </p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:ring-ta-green focus:border-ta-green"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white border-b border-gray-200 sticky top-14 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              {t('pages.safariPackages.filters')}
              {isFilterOpen ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </button>
          </div>

          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.priceRange')}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="6000"
                      step="100"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-ta-green"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.duration')}</h3>
                  <div className="space-y-2">
                    {['4-6 days', '7-10 days', '11-15 days', '15+ days'].map((duration) => (
                      <label key={duration} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={durationFilter.includes(duration)}
                          onChange={() => {
                            if (durationFilter.includes(duration)) {
                              setDurationFilter(durationFilter.filter(d => d !== duration));
                            } else {
                              setDurationFilter([...durationFilter, duration]);
                            }
                          }}
                          className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                        />
                        <span className="ml-2 text-sm text-gray-700">{duration}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-6">
            {filteredPackages.map((pkg) => (
              <div 
                key={pkg.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-32 sm:h-48">
                  <img 
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover"
                  />
                  <button className="absolute top-2 right-2 p-1 sm:p-1.5 bg-white/80 rounded-full hover:bg-white transition-colors">
                    <Heart className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600 hover:text-red-500" />
                  </button>
                  {pkg.tag && (
                    <div className="absolute top-2 left-2 bg-ta-green text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                      {pkg.tag}
                    </div>
                  )}
                </div>

                <div className="p-2 sm:p-4">
                  <div className="flex items-center mb-1 sm:mb-2">
                    <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 fill-current" />
                    <span className="ml-1 text-xs sm:text-sm font-medium">{pkg.rating}</span>
                    <span className="mx-1 text-gray-400 text-xs">•</span>
                    <span className="text-xs text-gray-600">{pkg.reviews} {t('common.reviews')}</span>
                  </div>

                  <h3 className="text-xs sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-2">{pkg.title}</h3>

                  <div className="space-y-1 mb-2 sm:mb-4">
                    <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-ta-green mr-1" />
                      {pkg.location}
                    </div>
                    <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                      <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-ta-green mr-1" />
                      {pkg.duration}
                    </div>
                    <div className="flex items-center text-[10px] sm:text-sm text-gray-600">
                      <Users className="h-3 w-3 sm:h-4 sm:w-4 text-ta-green mr-1" />
                      {pkg.groupSize}
                    </div>
                  </div>

                  <p className="text-[10px] sm:text-sm text-gray-600 mb-2 sm:mb-4 line-clamp-2">{pkg.description}</p>

                  <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
                    {pkg.highlights.map((highlight, index) => (
                      <span 
                        key={index}
                        className="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-ta-green/10 text-ta-green rounded-full text-[8px] sm:text-xs"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[8px] sm:text-xs text-gray-500">{t('common.from')}</p>
                      <p className="text-sm sm:text-xl font-bold text-gray-900">${pkg.price}</p>
                    </div>
                    <div className="flex space-x-1 sm:space-x-2">
                      <Link
                        to={getPackageRoute(pkg.id)}
                        className="px-2 py-1 sm:px-4 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-[10px] sm:text-sm"
                      >
                        {t('pages.safariPackages.viewDetails')}
                      </Link>
                      <button
                        onClick={() => handleBookNow(pkg)}
                        className="px-2 py-1 sm:px-4 sm:py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors text-[10px] sm:text-sm"
                      >
                        {t('pages.safariPackages.bookNow')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedPackage && (
        <BookingModal
          experience={selectedPackage}
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedPackage(null);
          }}
        />
      )}

      <Footer />
    </>
  );
};

export default SafariPackages;